import { useMutation, useQueryClient } from "@tanstack/react-query";
import { SchoolModel } from "model/SchoolModel";

import { api } from "~utils";

import { ClosureField } from "./ClosureFieldRow";
import { SchoolClosuresView } from "./SchoolClosuresView";

type SchoolClosuresProps = {
  school: SchoolModel;
};

export function SchoolClosures(props: SchoolClosuresProps) {
  const { school } = props;
  const { id, closures: schoolClosures } = school;

  const queryClient = useQueryClient();

  function invalidateQueries() {
    queryClient.invalidateQueries({ queryKey: ["schools", id] });
  }

  const { mutate: createClosures } = useMutation({
    mutationKey: ["schools", id, "closures", "create"],
    mutationFn: async (payload: Omit<ClosureField, "id">[]) => {
      const { data } = await api({
        method: "POST",
        url: `api/admin/schools/${id}/closures`,
        data: payload,
      });
      return data;
    },
    onSuccess: () => invalidateQueries(),
  });

  const { mutate: updateClosures } = useMutation({
    mutationKey: ["schools", id, "closures", "update"],
    mutationFn: async (payload: ClosureField[]) => {
      const { data } = await api({
        method: "PUT",
        url: `api/admin/schools/${id}/closures`,
        data: payload,
      });
      return data;
    },
    onSuccess: () => invalidateQueries(),
  });

  const { mutate: deleteClosures } = useMutation({
    mutationKey: ["schools", id, "closures", "delete"],
    mutationFn: async (payload: string[]) => {
      const { data } = await api({
        method: "DELETE",
        url: `api/admin/schools/${id}/closures`,
        data: payload,
      });
      return data;
    },
    onSuccess: () => invalidateQueries(),
  });

  async function handleSubmit(closures: ClosureField[]) {
    const removeItems = closures.filter((closure) => closure.isRemoved && closure.id);
    const newItems = closures.filter((closure) => !closure.isRemoved && !closure.id);
    const updateItems = closures.filter((closure) => closure.id && !closure.isRemoved);

    // Stores actions that we want to perform.
    const actions = [];

    if (newItems.length > 0) {
      actions.push(createClosures(newItems));
    }
    if (removeItems.length > 0) {
      actions.push(deleteClosures(removeItems.map((c) => c.id)));
    }
    if (updateItems.length > 0) {
      // Need to filter out any closures that do NOT need to be updated
      const finalUpdateItems = updateItems.filter((item) => {
        const itemObj = schoolClosures.find((obj) => obj.id === item.id);
        if (!(JSON.stringify(item) === JSON.stringify(itemObj))) {
          return item;
        }
      });

      if (finalUpdateItems.length > 0) {
        actions.push(updateClosures(finalUpdateItems));
      }
    }

    await Promise.all(actions);
  }

  return <SchoolClosuresView school={school} onSubmit={handleSubmit} />;
}
